/**
 * Motor del workshop: recorre capítulos y beats, habla (mp3 pregenerados con
 * Polly), mueve el cursor guía y opera la pantalla. Sin React: es un store
 * (`subscribe`/`getState`) que el reproductor lee con useSyncExternalStore.
 *
 * Cancelación con contador de generación (patrón de useSpokenReply): pausar,
 * saltar de capítulo o salir incrementan `gen`; cualquier `sleep` viejo tira
 * `Cancelled` y la corrida anterior se apaga sola.
 */
import type { QueryClient } from '@tanstack/react-query';
import { createAudioPlaybackFromUrl, type ControllableAudio } from '@/lib/speech-utils';
import { centerOf, findTarget, humanClick, isTextField, pressKey, scrollIntoViewIfNeeded, setNativeValue } from './dom';
import type { Action, Chapter, DemoOp, Target, Workshop, WorkshopManifest, WorkshopSettings } from './types';

export type WorkshopStatus = 'idle' | 'playing' | 'paused' | 'ended';

export interface CursorState {
  x: number;
  y: number;
  visible: boolean;
  /** Contador: cada clic lo incrementa y el cursor anima la onda. */
  clicks: number;
}

export interface EngineState {
  status: WorkshopStatus;
  chapter: number;
  beat: number;
  /** 0..1 sobre el total de beats. */
  progress: number;
  subtitle: string | null;
  card: { n: number; total: number; title: string; subtitle: string } | null;
  overlay: 'rules' | null;
  cursor: CursorState;
}

export interface EngineDeps {
  workshop: Workshop;
  manifest: WorkshopManifest;
  settings: () => WorkshopSettings;
  navigate: (to: string) => void;
  applyDemo: (op: DemoOp) => void;
  queryClient: QueryClient;
  audioUrl: (beatId: string) => string;
  musicUrl: string;
  reduceMotion: boolean;
}

class Cancelled extends Error {
  constructor() {
    super('cancelled');
  }
}

const TICK = 40;
const CARD_MS = 2600;
const MUSIC_VOLUME = 0.11;

export class WorkshopEngine {
  private state: EngineState;
  private listeners = new Set<() => void>();
  private gen = 0;
  private paused = false;
  private audio: ControllableAudio | null = null;
  private music: HTMLAudioElement | null = null;
  private clipboardStubbed = false;
  private fadeTimer: number | null = null;
  private beatIndex = 0;
  private readonly totalBeats: number;

  constructor(private deps: EngineDeps) {
    this.totalBeats = deps.workshop.chapters.reduce((n, c) => n + c.beats.length, 0);
    this.state = {
      status: 'idle',
      chapter: 0,
      beat: 0,
      progress: 0,
      subtitle: null,
      card: null,
      overlay: null,
      cursor: { x: window.innerWidth / 2, y: window.innerHeight / 2, visible: false, clicks: 0 },
    };
  }

  // ─── store ────────────────────────────────────────────────────────────────

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  getState = () => this.state;

  private set(patch: Partial<EngineState>) {
    this.state = { ...this.state, ...patch };
    for (const fn of this.listeners) fn();
  }

  private get speed() {
    return this.deps.settings().speed;
  }

  // ─── controles ────────────────────────────────────────────────────────────

  start(fromChapter = 0) {
    this.cancel();
    this.paused = false;
    this.beatIndex = this.deps.workshop.chapters.slice(0, fromChapter).reduce((n, c) => n + c.beats.length, 0);
    this.set({ status: 'playing', chapter: fromChapter, beat: 0, subtitle: null, card: null, overlay: null, progress: this.beatIndex / this.totalBeats });
    this.stubClipboard();
    this.startMusic();
    void this.run(++this.gen, fromChapter);
  }

  /**
   * Los "Copiar" de la demo no deben tocar el portapapeles real (ni fallar:
   * un clic sintético no tiene activación de usuario y Chrome rechazaría el
   * writeText con un toast de error en medio del workshop).
   */
  private stubClipboard() {
    if (this.clipboardStubbed || !('clipboard' in navigator)) return;
    try {
      Object.defineProperty(navigator.clipboard, 'writeText', { configurable: true, value: async () => undefined });
      this.clipboardStubbed = true;
    } catch {
      // no se pudo: el toast "No se pudo copiar" es tolerable
    }
  }

  private restoreClipboard() {
    if (!this.clipboardStubbed) return;
    Reflect.deleteProperty(navigator.clipboard, 'writeText');
    this.clipboardStubbed = false;
  }

  pause() {
    if (this.state.status !== 'playing') return;
    this.paused = true;
    this.audio?.pause();
    this.music?.pause();
    this.set({ status: 'paused' });
  }

  resume() {
    if (this.state.status !== 'paused') return;
    this.paused = false;
    this.audio?.resume();
    if (this.deps.settings().music) void this.music?.play().catch(() => undefined);
    this.set({ status: 'playing' });
  }

  togglePause() {
    if (this.state.status === 'playing') this.pause();
    else if (this.state.status === 'paused') this.resume();
  }

  nextChapter() {
    const next = this.state.chapter + 1;
    if (next >= this.deps.workshop.chapters.length) {
      this.finish();
      return;
    }
    this.start(next);
  }

  /** Cambios de velocidad / música en caliente. */
  applySettings() {
    const s = this.deps.settings();
    this.audio?.setRate(s.speed);
    if (this.music) {
      if (s.music && this.state.status === 'playing') void this.music.play().catch(() => undefined);
      else this.music.pause();
    }
  }

  /** Apagar todo (salir del workshop o desmontar). */
  destroy() {
    // primero lo que toca globales de la app (portapapeles), después lo propio
    this.restoreClipboard();
    this.cancel();
    this.stopMusic();
    this.set({ status: 'idle', subtitle: null, card: null, overlay: null, cursor: { ...this.state.cursor, visible: false } });
  }

  private cancel() {
    this.gen++;
    this.audio?.stop();
    this.audio = null;
  }

  private finish() {
    this.cancel();
    this.set({ status: 'ended', subtitle: null, card: null, progress: 1, cursor: { ...this.state.cursor, visible: false } });
    if (this.music) {
      // que la música baje sola al cerrar
      const m = this.music;
      this.fadeTimer = window.setInterval(() => {
        m.volume = Math.max(0, m.volume - 0.01);
        if (m.volume <= 0.005) {
          this.clearFade();
          m.pause();
        }
      }, 120);
    }
  }

  private clearFade() {
    if (this.fadeTimer !== null) window.clearInterval(this.fadeTimer);
    this.fadeTimer = null;
  }

  // ─── música ───────────────────────────────────────────────────────────────

  private startMusic() {
    this.clearFade();
    if (!this.music) {
      this.music = new Audio(this.deps.musicUrl);
      this.music.loop = true;
      this.music.preload = 'auto';
    }
    this.music.volume = MUSIC_VOLUME;
    if (this.deps.settings().music) void this.music.play().catch(() => undefined);
  }

  private stopMusic() {
    this.clearFade();
    if (!this.music) return;
    this.music.pause();
    this.music.src = '';
    this.music = null;
  }

  // ─── reloj pausable ───────────────────────────────────────────────────────

  private async sleep(ms: number, gen: number) {
    let left = ms / this.speed;
    while (left > 0) {
      if (gen !== this.gen) throw new Cancelled();
      await new Promise((r) => window.setTimeout(r, TICK));
      if (!this.paused) left -= TICK;
    }
    if (gen !== this.gen) throw new Cancelled();
  }

  private check(gen: number) {
    if (gen !== this.gen) throw new Cancelled();
  }

  // ─── corrida ──────────────────────────────────────────────────────────────

  private async run(gen: number, fromChapter: number) {
    const { chapters } = this.deps.workshop;
    try {
      for (let c = fromChapter; c < chapters.length; c++) {
        this.set({ chapter: c, beat: 0 });
        await this.playChapter(chapters[c], c, gen);
      }
      this.finish();
    } catch (e) {
      if (!(e instanceof Cancelled)) {
        // un target que no apareció no debe colgar el workshop: se sigue con el siguiente capítulo
        console.error('[vault-workshop]', e);
        if (gen === this.gen) this.nextChapter();
      }
    }
  }

  private async playChapter(ch: Chapter, index: number, gen: number) {
    this.set({ subtitle: null, cursor: { ...this.state.cursor, visible: false } });
    await this.runActions(ch.setup, gen);
    this.set({ card: { n: index + 1, total: this.deps.workshop.chapters.length, title: ch.title, subtitle: ch.subtitle } });
    await this.sleep(CARD_MS, gen);
    this.set({ card: null });
    await this.sleep(350, gen);
    for (let b = 0; b < ch.beats.length; b++) {
      const beat = ch.beats[b];
      this.set({ beat: b, progress: this.beatIndex / this.totalBeats, overlay: beat.overlay ?? this.state.overlay });
      await this.playBeat(beat.id, beat.say, beat.do, gen);
      this.beatIndex++;
      this.set({ progress: this.beatIndex / this.totalBeats });
      await this.sleep(beat.hold ?? 600, gen);
    }
  }

  private async playBeat(id: string, say: string, actions: Action[] | undefined, gen: number) {
    const s = this.deps.settings();
    this.set({ subtitle: s.subtitles ? say : null });
    const estimate = (this.deps.manifest.beats[id]?.dur ?? Math.max(2.5, say.length / 15)) * 1000;

    const narration = (async () => {
      if (!s.voice) {
        await this.sleep(estimate, gen);
        return;
      }
      const t0 = performance.now();
      const audio = createAudioPlaybackFromUrl(this.deps.audioUrl(id), { rate: s.speed });
      this.audio = audio;
      if (this.paused) audio.pause();
      await audio.done;
      this.check(gen);
      // mp3 ausente / bloqueado (Safari sin gesto): el subtítulo igual se lee entero
      if (performance.now() - t0 < 400) await this.sleep(estimate, gen);
    })();

    const doing = this.runActions(actions, gen).catch((e) => {
      if (e instanceof Cancelled) throw e;
      console.warn('[vault-workshop] acción fallida en', id, e);
    });

    await Promise.all([narration, doing]);
    this.audio = null;
  }

  private async runActions(actions: Action[] | undefined, gen: number) {
    for (const a of actions ?? []) {
      this.check(gen);
      await this.runAction(a, gen);
    }
  }

  private async runAction(a: Action, gen: number) {
    if ('go' in a) {
      this.deps.navigate(a.go);
      await this.sleep(400, gen);
      return;
    }
    if ('wait' in a) {
      await this.sleep(a.wait, gen);
      return;
    }
    if ('demo' in a) {
      this.deps.applyDemo(a.demo);
      if (a.refresh !== false) {
        await this.deps.queryClient.invalidateQueries();
        await this.sleep(250, gen);
      }
      return;
    }
    if ('key' in a) {
      pressKey(a.key);
      await this.sleep(400, gen);
      return;
    }
    if ('waitFor' in a) {
      await this.resolve(a.waitFor, gen, a.timeout ?? 6000);
      return;
    }
    if ('scroll' in a) {
      const el = await this.resolve(a.scroll, gen, 4000);
      if (el && scrollIntoViewIfNeeded(el, !this.deps.reduceMotion)) await this.sleep(450, gen);
      return;
    }
    if ('move' in a) {
      const el = await this.moveTo(a.move, gen, a.timeout);
      if (el && a.press) await this.pressAt(gen);
      return;
    }
    if ('click' in a) {
      const el = await this.moveTo(a.click, gen);
      if (!el) return;
      await this.pressAt(gen);
      humanClick(el);
      await this.sleep(350, gen);
      return;
    }
    if ('type' in a) {
      await this.typeInto(a.type[0], a.type[1], a.cps, gen);
      return;
    }
    if ('drop' in a) {
      await this.dropImage(a.drop[0], a.drop[1], gen);
      return;
    }
  }

  /**
   * Arrastra una imagen del bundle hasta el target y la suelta (DragEvent con
   * DataTransfer, que es lo que React lee en onDrop). Donde el navegador no
   * construye DragEvent con archivos (Safari), se carga por el <input type=file>
   * más cercano: el resultado para el asistente es el mismo.
   */
  private async dropImage(target: Target, url: string, gen: number) {
    const el = await this.moveTo(target, gen);
    if (!el) return;
    const blob = await fetch(url).then((r) => r.blob());
    this.check(gen);
    const file = new File([blob], url.split('/').pop() ?? 'captura.png', { type: blob.type || 'image/png' });
    const dt = new DataTransfer();
    dt.items.add(file);
    let dropped = false;
    if (typeof DragEvent === 'function') {
      try {
        el.dispatchEvent(new DragEvent('dragover', { bubbles: true, cancelable: true, dataTransfer: dt }));
        await this.sleep(500, gen);
        // dispatchEvent devuelve false cuando el handler hizo preventDefault: o sea, cuando React lo atendió
        dropped = !el.dispatchEvent(new DragEvent('drop', { bubbles: true, cancelable: true, dataTransfer: dt }));
      } catch {
        dropped = false;
      }
    }
    if (!dropped) {
      const input = (el.closest('[role="dialog"]') ?? document).querySelector<HTMLInputElement>('input[type="file"]');
      if (!input) return;
      input.files = dt.files;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    await this.sleep(350, gen);
  }

  private async typeInto(target: Target, text: string, cps: number | undefined, gen: number) {
    const el = await this.moveTo(target, gen);
    if (!el || !isTextField(el)) return;
    await this.pressAt(gen);
    humanClick(el);
    el.focus();
    const perChar = 1000 / (cps ?? 18);
    for (let i = 1; i <= text.length; i++) {
      setNativeValue(el, text.slice(0, i));
      await this.sleep(perChar, gen);
    }
  }

  /** Espera a que el target aparezca (la pantalla puede estar cargando). */
  private async resolve(target: Target, gen: number, timeout: number): Promise<HTMLElement | null> {
    const deadline = performance.now() + timeout;
    while (performance.now() < deadline) {
      const el = findTarget(target);
      if (el) return el;
      await this.sleep(100, gen);
    }
    console.warn('[vault-workshop] no apareció', target);
    return null;
  }

  private async moveTo(target: Target, gen: number, timeout = 5000): Promise<HTMLElement | null> {
    const el = await this.resolve(target, gen, timeout);
    if (!el) return null;
    if (scrollIntoViewIfNeeded(el, !this.deps.reduceMotion)) await this.sleep(450, gen);
    const to = centerOf(el);
    const from = this.state.cursor;
    const dist = Math.hypot(to.x - from.x, to.y - from.y);
    this.set({ cursor: { ...from, x: to.x, y: to.y, visible: this.deps.settings().cursor } });
    // el cursor viaja a velocidad constante (framer lo anima con la misma duración: ver FakeCursor)
    await this.sleep(this.deps.reduceMotion ? 80 : Math.min(900, 300 + dist * 0.45), gen);
    return el;
  }

  private async pressAt(gen: number) {
    this.set({ cursor: { ...this.state.cursor, clicks: this.state.cursor.clicks + 1 } });
    await this.sleep(220, gen);
  }
}

/** Duración estimada de un capítulo (tarjeta + audios + holds), en segundos. */
export function chapterSeconds(ch: Chapter, manifest: WorkshopManifest): number {
  const beats = ch.beats.reduce((n, b) => n + (manifest.beats[b.id]?.dur ?? Math.max(2.5, b.say.length / 15)) + (b.hold ?? 600) / 1000, 0);
  return CARD_MS / 1000 + beats;
}

export const fmtSeconds = (s: number) => {
  const t = Math.round(s);
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
};
